'use client'

import { useState, useEffect, useCallback } from 'react'
import { GOOGLE_CLIENT_ID } from '@/lib/config'
import { event } from '@/lib/ga'

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string
            callback: (response: { credential: string }) => void
            auto_select?: boolean
            cancel_on_tap_outside?: boolean
          }) => void
          prompt: (momentNotification?: (notification: { isNotMoment: () => boolean }) => void) => void
          renderButton: (element: HTMLElement, config: {
            type: string
            theme: string
            size: string
            text: string
            shape: string
            logo_alignment: string
            width: string
            locale: string
          }) => void
        }
      }
    }
  }
}

interface UseGoogleAuthReturn {
  handleGoogleLogin: () => Promise<string | null>
  isLoading: boolean
  error: string | null
}

export function useGoogleAuth(): UseGoogleAuthReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isScriptLoaded, setIsScriptLoaded] = useState(false)

  // Load Google Identity Services script
  useEffect(() => {
    if (typeof window === 'undefined' || !GOOGLE_CLIENT_ID) {
      return
    }

    // Check if script is already loaded
    if (window.google?.accounts?.id) {
      setIsScriptLoaded(true)
      return
    }

    // Check if script tag already exists
    const existingScript = document.querySelector('script[src*="accounts.google.com/gsi/client"]')
    if (existingScript) {
      // Wait for script to load
      const checkGoogle = setInterval(() => {
        if (window.google?.accounts?.id) {
          setIsScriptLoaded(true)
          clearInterval(checkGoogle)
        }
      }, 100)
      return () => clearInterval(checkGoogle)
    }

    // Load the script
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => {
      // Wait a bit for Google to initialize
      setTimeout(() => {
        if (window.google?.accounts?.id) {
          setIsScriptLoaded(true)
        }
      }, 100)
    }
    script.onerror = () => {
      setError('Failed to load Google Identity Services')
      setIsScriptLoaded(false)
    }
    document.head.appendChild(script)
  }, [])

  const handleGoogleLogin = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      if (!GOOGLE_CLIENT_ID) {
        setError('Google Client ID is not configured')
        resolve(null)
        return
      }

      if (!isScriptLoaded) {
        setError('Google Identity Services is still loading. Please try again.')
        resolve(null)
        return
      }

      if (!window.google?.accounts?.id) {
        setError('Google Identity Services is not available')
        resolve(null)
        return
      }

      // Type guard: we know window.google.accounts.id exists after the check above
      const googleAccounts = window.google.accounts.id

      setIsLoading(true)
      setError(null)
      event('login_attempt')

      let callbackExecuted = false
      let tempButtonContainer: HTMLElement | null = null
      let cleanupTimeout: NodeJS.Timeout | null = null

      const cleanup = () => {
        if (callbackExecuted) return

        callbackExecuted = true
        setIsLoading(false)

        if (tempButtonContainer && tempButtonContainer.parentNode) {
          tempButtonContainer.parentNode.removeChild(tempButtonContainer)
          tempButtonContainer = null
        }

        if (cleanupTimeout) {
          clearTimeout(cleanupTimeout)
          cleanupTimeout = null
        }
      }

      try {
        // Create a temporary hidden container for Google's button
        tempButtonContainer = document.createElement('div')
        tempButtonContainer.style.position = 'fixed'
        tempButtonContainer.style.left = '-9999px'
        tempButtonContainer.style.top = '-9999px'
        tempButtonContainer.style.width = '1px'
        tempButtonContainer.style.height = '1px'
        tempButtonContainer.style.opacity = '0'
        tempButtonContainer.style.pointerEvents = 'none'
        document.body.appendChild(tempButtonContainer)

        // Initialize Google Sign-In with callback
        googleAccounts.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            if (callbackExecuted) return
            callbackExecuted = true
            setIsLoading(false)
            
            if (tempButtonContainer && tempButtonContainer.parentNode) {
              tempButtonContainer.parentNode.removeChild(tempButtonContainer)
            }

            if (cleanupTimeout) {
              clearTimeout(cleanupTimeout)
            }

            if (response.credential) {
              event('login_success')
              resolve(response.credential) // This is the idToken
            } else {
              const errorMsg = 'Failed to get Google ID token'
              setError(errorMsg)
              event('login_failure', { error_message: errorMsg })
              resolve(null)
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
        })

        // Render Google's button on the hidden container
        googleAccounts.renderButton(tempButtonContainer, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: '200',
          locale: 'en',
        })

        // Wait a moment for the button to render, then try to click it.
        setTimeout(() => {
          if (callbackExecuted) return

          const googleButton = tempButtonContainer?.querySelector('div[role="button"]') as HTMLElement
          if (googleButton) {
            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window,
            })
            googleButton.dispatchEvent(clickEvent)
          } else if (tempButtonContainer) {
            tempButtonContainer.click()
          } else if (window.google?.accounts?.id) {
            window.google.accounts.id.prompt()
          }

          cleanupTimeout = setTimeout(() => {
            if (!callbackExecuted) {
              cleanup()
              setError('Login timeout. Please try again.')
              resolve(null)
            }
          }, 120000)
        }, 100)
      } catch (err) {
        cleanup()
        const errorMsg = err instanceof Error ? err.message : 'An error occurred during Google login'
        setError(errorMsg)
        event('login_failure', { error_message: errorMsg })
        resolve(null)
      }
    })
  }, [isScriptLoaded])

  return {
    handleGoogleLogin,
    isLoading,
    error,
  }
}

